import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SideInvoice {
  id: string;
  invoiceNumber?: string | number | null;
  totalAmount?: string | number | null;
  eventRate?: string | number | null;
  discount?: string | number | null;
  depositRequired?: boolean | null;
  depositAmountCents?: number | string | null;
  depositPaidAt?: string | null;
  depositMethod?: string | null;
  depositNotes?: string | null;
  datePaid?: string | null;
  paymentType?: string | null;
  paymentNotes?: string | null;
}

interface InvoicePaymentDialogProps {
  open: boolean;
  invoice: SideInvoice | null;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
  onBalanceSaved?: (invoice: SideInvoice) => void;
}

const PAY_METHODS = [
  "Stripe",
  "PayPal",
  "ACH",
  "Cash",
  "Check",
  "Zelle",
  "Venmo",
  "Other",
];

function todayLocalISO() {
  return new Date().toISOString().split("T")[0];
}

export function InvoicePaymentDialog({
  open,
  invoice,
  onOpenChange,
  onSaved,
  onBalanceSaved,
}: InvoicePaymentDialogProps) {
  const { toast } = useToast();
  const [depDate, setDepDate] = useState("");
  const [depMethod, setDepMethod] = useState("");
  const [depNotes, setDepNotes] = useState("");
  const [balDate, setBalDate] = useState("");
  const [balMethod, setBalMethod] = useState("");
  const [balNotes, setBalNotes] = useState("");

  useEffect(() => {
    if (!open || !invoice) return;
    setDepDate(
      invoice.depositPaidAt
        ? new Date(invoice.depositPaidAt).toISOString().split("T")[0]
        : todayLocalISO(),
    );
    setDepMethod(invoice.depositMethod || "");
    setDepNotes(invoice.depositNotes || "");
    setBalDate(
      invoice.datePaid
        ? new Date(invoice.datePaid).toISOString().split("T")[0]
        : todayLocalISO(),
    );
    setBalMethod(invoice.paymentType || "");
    setBalNotes(invoice.paymentNotes || "");
  }, [open, invoice?.id]);

  const depositRequired = !!invoice?.depositRequired;
  const depositPaid = !!invoice?.depositPaidAt;
  const balancePaid = !!invoice?.datePaid;

  const depositSaveMutation = useMutation({
    mutationFn: async () => {
      if (!invoice) throw new Error("missing invoice");
      const res = await apiRequest(
        "PATCH",
        `/api/invoices/${invoice.id}/deposit`,
        {
          depositPaidAt: depDate ? new Date(depDate).toISOString() : null,
          depositMethod: depMethod || null,
          depositNotes: depNotes.trim() || null,
        },
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      if (invoice?.id) {
        queryClient.invalidateQueries({
          queryKey: ["/api/invoices", invoice.id, "dot-status"],
        });
      }
      toast({
        title: "Deposit recorded",
        description: "Deposit payment has been saved.",
      });
      onSaved?.();
    },
    onError: (e: Error) =>
      toast({
        title: "Could not save deposit",
        description: e.message,
        variant: "destructive",
      }),
  });

  const balanceSaveMutation = useMutation({
    mutationFn: async () => {
      if (!invoice) throw new Error("missing invoice");
      const res = await apiRequest(
        "PATCH",
        `/api/invoices/${invoice.id}/balance`,
        {
          datePaid: balDate ? new Date(balDate).toISOString() : null,
          paymentType: balMethod || null,
          paymentNotes: balNotes.trim() || null,
        },
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      if (invoice?.id) {
        queryClient.invalidateQueries({
          queryKey: ["/api/invoices", invoice.id, "dot-status"],
        });
      }
      toast({
        title: "Balance recorded",
        description: "Final payment has been saved.",
      });
      onSaved?.();
      if (invoice) onBalanceSaved?.(invoice);
    },
    onError: (e: Error) =>
      toast({
        title: "Could not save balance",
        description: e.message,
        variant: "destructive",
      }),
  });

  if (!invoice) return null;

  const depositCents = Number(invoice.depositAmountCents || 0);
  // Prefer eventRate - discount (the fields actually present on invoice rows).
  // Fall back to totalAmount for any callers that pre-compute it.
  const totalNum = invoice.eventRate != null
    ? Math.max(0, Number(invoice.eventRate || 0) - Number(invoice.discount || 0))
    : Number(invoice.totalAmount || 0);
  const depositLabel = depositCents
    ? `$${(depositCents / 100).toFixed(2)}`
    : "—";
  const balanceLabel = depositCents
    ? `$${Math.max(0, totalNum - depositCents / 100).toFixed(2)}`
    : `$${totalNum.toFixed(2)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Payment Details</DialogTitle>
          <DialogDescription>
            Record deposit and balance payments for invoice #
            {invoice.invoiceNumber}
          </DialogDescription>
        </DialogHeader>

        <div
          className={`grid gap-4 py-2 ${depositRequired ? "sm:grid-cols-2" : "grid-cols-1"}`}
        >
          {depositRequired && (
            <section
              className="rounded-lg border p-4 space-y-3"
              data-testid="section-deposit-side"
            >
              <header className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Deposit</h4>
                  <p className="text-xs text-muted-foreground">
                    Due now • {depositLabel}
                  </p>
                </div>
                {depositPaid && (
                  <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                    <CheckCircle2 className="h-4 w-4" /> Received
                  </span>
                )}
              </header>

              <div className="space-y-1">
                <Label htmlFor="dep-date">Date received</Label>
                <Input
                  id="dep-date"
                  type="date"
                  value={depDate}
                  onChange={(e) => setDepDate(e.target.value)}
                  data-testid="input-deposit-date"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="dep-method">Method</Label>
                <Select value={depMethod} onValueChange={setDepMethod}>
                  <SelectTrigger
                    id="dep-method"
                    data-testid="select-deposit-method"
                  >
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAY_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="dep-notes">
                  Notes{" "}
                  {depMethod === "Other" && (
                    <span className="text-destructive">*</span>
                  )}
                </Label>
                <Textarea
                  id="dep-notes"
                  rows={2}
                  value={depNotes}
                  onChange={(e) => setDepNotes(e.target.value)}
                  data-testid="input-deposit-notes"
                  placeholder={
                    depMethod === "Other"
                      ? "Required for Other"
                      : "Optional"
                  }
                />
              </div>
              <Button
                className="w-full"
                disabled={
                  !depDate ||
                  !depMethod ||
                  (depMethod === "Other" && !depNotes.trim()) ||
                  depositSaveMutation.isPending
                }
                onClick={() => depositSaveMutation.mutate()}
                data-testid="button-save-deposit"
              >
                {depositSaveMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {depositPaid ? "Update deposit" : "Save deposit"}
              </Button>
            </section>
          )}

          <section
            className="rounded-lg border p-4 space-y-3"
            data-testid="section-balance-side"
          >
            <header className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">
                  {depositRequired ? "Balance" : "Payment"}
                </h4>
                <p className="text-xs text-muted-foreground">
                  Remaining • {balanceLabel}
                </p>
              </div>
              {balancePaid && (
                <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                  <CheckCircle2 className="h-4 w-4" /> Received
                </span>
              )}
            </header>

            <div className="space-y-1">
              <Label htmlFor="bal-date">Date received</Label>
              <Input
                id="bal-date"
                type="date"
                value={balDate}
                onChange={(e) => setBalDate(e.target.value)}
                data-testid="input-balance-date"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bal-method">Method</Label>
              <Select value={balMethod} onValueChange={setBalMethod}>
                <SelectTrigger
                  id="bal-method"
                  data-testid="select-balance-method"
                >
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {PAY_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="bal-notes">
                Notes{" "}
                {balMethod === "Other" && (
                  <span className="text-destructive">*</span>
                )}
              </Label>
              <Textarea
                id="bal-notes"
                rows={2}
                value={balNotes}
                onChange={(e) => setBalNotes(e.target.value)}
                data-testid="input-balance-notes"
                placeholder={
                  balMethod === "Other" ? "Required for Other" : "Optional"
                }
              />
            </div>
            <Button
              className="w-full"
              disabled={
                !balDate ||
                !balMethod ||
                (balMethod === "Other" && !balNotes.trim()) ||
                balanceSaveMutation.isPending
              }
              onClick={() => balanceSaveMutation.mutate()}
              data-testid="button-save-balance"
            >
              {balanceSaveMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {balancePaid ? "Update payment" : "Save payment"}
            </Button>
          </section>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="button-close-payment-dialog"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
